import {
  Order,
  OrderCancelByPharmParams,
  OrderPickupCallParams,
  OrderSetPriceParams,
  PreparingOrdersParams,
  RequestedOrdersParams,
  SettlementsParams
} from '@/apis/order/interface';
import api from '@/apis/order';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authState } from '@/recoil/auth';
import { useRecoilValue } from 'recoil';
import GlobalAlert from '@/components/GlobalAlert';
import { getErrorMessage } from '@/utils/error';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import { errors } from '@/constants/errors';
import { ExcelDownload } from '@/utils/excel';
import { getCommerceOrderInfo, getSearchProductsByPharmacist, startDelivery } from '@/utils/graphqlApis';
import * as bff_types from '@/api/bff_types';
import { DomainRequestContextRequestInput } from '@/api/bff_types';

export class PharmacyAdditionalOrder {
  additionalOrderPrice: number;
  orderItems: Array<{
    name: string;
    price: number;
    thumbnail: string;
    quantity: number;
  }>;
  constructor(order: {
    additionalOrderPrice: number;
    orderItems: Array<{
      name: string;
      price: number;
      thumbnail: string;
      quantity: number;
    }>;
  }) {
    this.additionalOrderPrice = order.additionalOrderPrice;
    this.orderItems = order.orderItems;
  }
}

const useOrderQuery = () => {
  const { token } = useRecoilValue(authState);
  const queryClient = useQueryClient();
  const history = useHistory();

  /** [주문] 주문 목록 조회 */
  const requestedOrdersQuery = (params: RequestedOrdersParams) => {
    return useQuery(
      ['requestedOrders', params],
      () => api.getRequestedOrders(token, params),
      {
        enabled: !!token,
        keepPreviousData: true,
        staleTime: 5000
      }
    );
  };

  const preparingOrdersQuery = (params: PreparingOrdersParams) => {
    return useQuery(
      ['preparingOrders', params],
      () => api.getPreparingOrders(token, params),
      {
        enabled: !!token,
        keepPreviousData: true,
        staleTime: 5000
      }
    );
  };

  /** [주문] 주문 디테일 조회 */
  const orderDetailQuery = (orderId: string) => {
    return useQuery<Order>(
      ['order', orderId],
      () => api.getOrderDetail(token, orderId),
      {
        enabled: !!orderId
      }
    );
  };
  /** [주문] 처방전 읽음 처리 */
  const orderReceivedMutation = useMutation(
    (orderId: string) => api.setOrderReceive(token, orderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  /** [주문] 처방전 가격 설정 */
  const orderPriceMutation = useMutation(
    (params: OrderSetPriceParams) => api.setOrderPrice(token, params),
    {
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      },
      onSettled: () => {
        queryClient.invalidateQueries(['order']);
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  /** [주문] 처방전 주문 취소 by pharmacy */
  const orderCancelByPharmMutation = useMutation(
    (params: OrderCancelByPharmParams) =>
      api.setOrderCancelByPharm(token, params),
    {
      onSuccess: () => {
        history.goBack();
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      },
      onError: () => {
        GlobalAlert.show({
          title: '이미 취소 또는 결제되어\n거절할 수 없어요',
          buttons: [{ text: '확인', onClick: () => window.location.reload() }]
        });
      }
    }
  );

  /** [주문] 처방전 주문 취소 by user */
  const orderCancelByUserMutation = useMutation(
    (orderId: number) => api.setOrderCancelByUser(token, orderId),
    {
      onSuccess: () => {
        history.goBack();
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  /** [주문] 약 배차 요청 */
  const orderPickupCallMutation = useMutation(
    (params: OrderPickupCallParams) => api.setOrderPickupCall(token, params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['order']);
      },
      onError: (err: AxiosError, variables: OrderPickupCallParams) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [주문] 약 수거 완료 / 배송 시작 */
  const orderStartDeliveryMutation = useMutation(
    (request: bff_types.OrderDeliveryStartMutationRequestInput) =>
      startDelivery(request, token),
    {
      onSuccess: () => {
        history.goBack();
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  /** [주문] 택배 내역 엑셀 다운로드 (CJ) */
  const orderCJExcelDownloadMutation = useMutation(
    (params: SettlementsParams) => api.onDownloadCJExcel(token, params),
    {
      onSuccess: (res: any, variables: SettlementsParams) => {
        ExcelDownload(res, variables);
      },
      onError: async (err: AxiosError) => {
        const errCode = JSON.parse(await err.response?.data?.text()).code;
        GlobalAlert.show({ title: errors[errCode] });
      }
    }
  );
  /** [주문] 추가주문 상품 목록 조회 */
  const getCommerceOrderQuery = (request: DomainRequestContextRequestInput) => {
    return useQuery(
      ['getCommerceOrderInfo', request],
      () => getCommerceOrderInfo(token, request),
      {
        enabled: !!token && !!request.key,
        select: response => {
          if (
            !response?.getCommerceOrdersByRequestContext ||
            !response.getCommerceOrdersByRequestContext.length
          ) {
            return {
              hasAdditionalOrder: false,
              additionalOrder: new PharmacyAdditionalOrder({
                additionalOrderPrice: 0,
                orderItems: []
              })
            };
          }
          const responseAdditionalOrder =
            response.getCommerceOrdersByRequestContext[0];
          return {
            hasAdditionalOrder: true,
            additionalOrder: new PharmacyAdditionalOrder({
              orderItems: responseAdditionalOrder.orderItems.map(item => ({
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                thumbnail: item.product.productThumbnailUrl
              })),
              additionalOrderPrice: responseAdditionalOrder.totalItemPrice
            })
          };
        }
      }
    );
  };

  /** [주문] 추가주문 상품 목록 조회 */
  const getSearchProductsByPharmacistQuery = () => {
    return useQuery(
      ['getSearchProductsByPharmacistQuery'],
      () => getSearchProductsByPharmacist(token),
      {
        enabled: !!token,
        select: response => {
          if (
            !response?.searchProductsByPharmacist ||
            !response.searchProductsByPharmacist.length
          ) {
            return [];
          }
          return response.searchProductsByPharmacist;
        }
      }
    );
  };

  return {
    requestedOrdersQuery,
    preparingOrdersQuery,
    orderDetailQuery,
    getCommerceOrderQuery,
    getSearchProductsByPharmacistQuery,
    orderReceivedMutation,
    orderPriceMutation,
    orderCancelByPharmMutation,
    orderCancelByUserMutation,
    orderPickupCallMutation,
    orderStartDeliveryMutation,
    orderCJExcelDownloadMutation
  };
};

export default useOrderQuery;
